import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { AuthShell } from '@/pages/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { publicApprovalApi, PublicApprovalError, type PublicApprovalPreview } from '@/lib/engagement-api';

const optionLabels: Record<string, string> = {
  subreddit: 'Subreddit', flair_id: 'Flair', flair_text: 'Flair-Text', nsfw: 'Für Erwachsene',
  spoiler: 'Spoiler', send_replies: 'Antworten senden', disable_comment: 'Kommentare deaktivieren',
  disable_duet: 'Duett deaktivieren', disable_stitch: 'Stitch deaktivieren', auto_add_music: 'Musik hinzufügen',
  video_cover_timestamp_ms: 'Titelbild bei Millisekunde', board_id: 'Pinnwand', language_code: 'Sprache', cta_type: 'Handlungsaufruf',
};
function safeUrl(value: string | null | undefined): string | undefined {
  return value && /^https?:\/\//i.test(value) ? value : undefined;
}
function dateLabel(value: string): string {
  return new Date(value).toLocaleString('de-CH');
}
function reviewHtml(value: string): string {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a', 'blockquote', 'code', 'pre', 'span', 'h1', 'h2', 'h3', 'img', 'video', 'audio', 'source'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'controls', 'poster', 'type'],
  });
}
function LinkPreview({ url }: { url?: string | null }) {
  const href = safeUrl(url);
  return href ? <a href={href} target="_blank" rel="noopener noreferrer" className="block break-all text-sm text-accent">{url}</a> : null;
}

export function ApprovePage({ token }: { token: string }) {
  return <ApprovalReview key={token} token={token} />;
}

function ApprovalReview({ token }: { token: string }) {
  const [preview, setPreview] = useState<PublicApprovalPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPreview(null); setError(null); setDone(null);
    void publicApprovalApi.preview(token).then((data) => {
      if (!cancelled) setPreview(data);
    }).catch((cause: unknown) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : 'Link konnte nicht geladen werden.');
    });
    return () => { cancelled = true; };
  }, [token, reload]);

  const decide = async (action: 'approve' | 'reject') => {
    if (!preview || busy || error) return;
    setBusy(true);
    try {
      await publicApprovalApi.decide(token, action, preview.revision);
      setDone(action === 'approve' ? 'Freigegeben.' : 'Abgelehnt.');
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Aktion fehlgeschlagen.';
      if (cause instanceof PublicApprovalError && cause.decisionSaved) setDone(message);
      else setError(message);
    } finally { setBusy(false); }
  };

  if (done) return <AuthShell title="Erledigt"><p role="status" className="text-sm text-foreground">{done}</p></AuthShell>;
  if (error) return <AuthShell title="Freigabe">
    <p role="alert" className="text-sm text-danger">{error}</p>
    <Button className="mt-4" variant="outline" onClick={() => setReload((value) => value + 1)}>Vorschau neu laden</Button>
  </AuthShell>;
  if (!preview) return <AuthShell title="Freigabe"><p className="text-sm text-muted">Wird geladen…</p></AuthShell>;

  return (
    <AuthShell title="Freigabe" description={preview.campaign_name || preview.platforms?.join(', ') || undefined}>
      <div className="min-w-0 space-y-5 break-words">
        <section className="space-y-2" aria-label="Beitrag">
          <h2 className="font-medium text-foreground">{preview.title}</h2>
          {preview.body && <p className="whitespace-pre-wrap text-sm text-foreground">{preview.body}</p>}
          {preview.content_md && preview.content_md !== preview.body && <div><h3 className="text-xs font-medium text-muted">Markdown</h3><p className="whitespace-pre-wrap text-sm">{preview.content_md}</p></div>}
          {preview.content_html && <div><h3 className="text-xs font-medium text-muted">Formatierter Inhalt</h3><div className="text-sm [&_img]:max-w-full [&_video]:max-w-full" dangerouslySetInnerHTML={{ __html: reviewHtml(preview.content_html) }} /></div>}
          <LinkPreview url={preview.link_url} />
          {preview.first_comment && <div><h3 className="text-xs font-medium text-muted">Erster Kommentar</h3><p className="whitespace-pre-wrap text-sm">{preview.first_comment}</p></div>}
          {preview.planned_at && <p className="text-xs text-muted">Geplant: {dateLabel(preview.planned_at)}</p>}
        </section>
        {!!preview.media?.length && <section className="space-y-3" aria-label="Medien">
          <h2 className="text-sm font-medium">Medien</h2>
          {preview.media.map((asset, index) => {
            const url = safeUrl(asset.url);
            return <figure key={asset.id + ':' + index} className="space-y-1">
              {url && asset.mime_type.startsWith('image/') ? <img src={url} alt={asset.filename} referrerPolicy="no-referrer" className="max-h-80 w-full rounded-lg border border-border object-contain" />
                : url && asset.mime_type.startsWith('video/') ? <video src={url} controls preload="metadata" className="max-h-80 w-full rounded-lg border border-border" />
                : <LinkPreview url={asset.url} />}
              <figcaption className="text-xs text-muted">{index + 1}. {asset.filename}{asset.role === 'thumbnail' ? ' · Titelbild' : ''}{!url ? ' · Vorschau nicht verfügbar' : ''}</figcaption>
            </figure>;
          })}
        </section>}
        {!!preview.targets?.length && <section className="space-y-3" aria-label="Kanäle">
          <h2 className="text-sm font-medium">Kanäle</h2>
          {preview.targets.map((target) => <article key={target.id} className="space-y-2 rounded-lg border border-border p-3">
            <h3 className="text-sm font-medium">{target.account_name} · {target.platform}</h3>
            <p className="text-xs text-muted">{target.post_kind}{target.status === 'cancelled' ? ' · Abgebrochen' : ''}</p>
            {target.title && <p className="text-sm font-medium">{target.title}</p>}
            <p className="whitespace-pre-wrap text-sm">{target.body || 'Ohne Text'}</p>
            {target.scheduled_at && <p className="text-xs text-muted">Geplant: {dateLabel(target.scheduled_at)}</p>}
            {target.privacy_status && <p className="text-xs text-muted">Sichtbarkeit: {target.privacy_status}</p>}
            {target.category_id && <p className="text-xs text-muted">Kategorie: {target.category_id}</p>}
            {target.platform === 'youtube' && <p className="text-xs text-muted">Abonnenten benachrichtigen: {target.notify_subscribers ? 'Ja' : 'Nein'}</p>}
            {Object.entries(target.options).map(([key, value]) => <p key={key} className="text-xs text-muted">{optionLabels[key] || key}: {typeof value === 'boolean' ? value ? 'Ja' : 'Nein' : typeof value === 'object' ? JSON.stringify(value) : String(value)}</p>)}
          </article>)}
        </section>}
        {preview.newsletter && <section aria-label="Newsletter" className="space-y-1 rounded-lg border border-border p-3">
          <h2 className="text-sm font-medium">Newsletter</h2>
          <p className="text-sm">{preview.newsletter.subject}</p>
          <p className="text-xs text-muted">Empfängerliste: {preview.newsletter.list_id}</p>
          <p className="text-xs text-muted">Absender: {preview.newsletter.from_name}</p>
          <p className="text-xs text-muted">Antwort an: {preview.newsletter.reply_to}</p>
        </section>}
        <div className="flex flex-wrap gap-2">
          <Button type="button" className="flex-1" disabled={busy} onClick={() => void decide('approve')}>Freigeben</Button>
          <Button type="button" variant="outline" className="flex-1" disabled={busy} onClick={() => void decide('reject')}>Ablehnen</Button>
        </div>
      </div>
    </AuthShell>
  );
}
